import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { packetsApi, bullhornApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Package, 
  User, 
  Building2, 
  Send, 
  RefreshCw,
  Search,
  FileText,
  CheckCircle2,
  Eye,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import debounce from 'lodash/debounce';

export default function SendPacketPage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const [packets, setPackets] = useState([]);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [bullhornConnected, setBullhornConnected] = useState(false);
  
  // Recipient search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  // Placement-mode state (only used when packet.entity_type === 'placement').
  // Holds the chosen placement record with its resolved candidate + contact
  // so we can route each template in the packet to the right party.
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  
  // Send options
  const [expiryHours, setExpiryHours] = useState(72);
  const [requiresApproval, setRequiresApproval] = useState(false);
  
  // Success state
  const [sendResult, setSendResult] = useState(null);
  // Test-preview dry run state — kept separate from the live send state
  // so the recruiter can preview as many times as they want without
  // affecting the Send button's spinner.
  const [previewing, setPreviewing] = useState(false);
  const [previewResults, setPreviewResults] = useState(null); // null | array

  useEffect(() => {
    loadPackets();
    checkBullhornStatus();
  }, []);

  const loadPackets = async () => {
    try {
      const response = await packetsApi.list();
      setPackets(response.data);
    } catch (error) {
      toast.error('Failed to load packets');
    } finally {
      setLoading(false);
    }
  };

  const checkBullhornStatus = async () => {
    try {
      const response = await bullhornApi.status();
      setBullhornConnected(response.data.connected);
    } catch (error) {
      setBullhornConnected(false);
    }
  };

  // Debounced search — covers candidate, contact, and placement modes.
  const performSearch = useCallback(
    debounce(async (query, entityType) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        let results = [];
        if (entityType === 'placement') {
          const r = await bullhornApi.searchPlacements(query);
          results = r.data?.placements || [];
        } else if (entityType === 'contact') {
          const r = await bullhornApi.searchContacts(query);
          results = r.data?.contacts || [];
        } else {
          const r = await bullhornApi.searchCandidates(query);
          results = r.data?.candidates || [];
        }
        setSearchResults(results);
      } catch (error) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (selectedPacket) {
      performSearch(query, selectedPacket.entity_type);
    }
  };

  const handlePacketSelect = (packetId) => {
    const packet = packets.find(p => p.id === packetId);
    setSelectedPacket(packet);
    setSelectedRecipient(null);
    setSelectedPlacement(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Single-recipient pick (candidate/contact mode).
  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
    setSelectedPlacement(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Placement pick — surfaces the resolved candidate + contact so the
  // recruiter can confirm both parties before sending. The actual routing
  // (which template goes to whom) happens server-side.
  const handlePlacementSelect = (placement) => {
    setSelectedPlacement(placement);
    setSelectedRecipient(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Both handleSend and handleTestPreview need the exact same payload
  // shape (so a preview reflects what the real send would do). Extract
  // the build logic into a single helper to keep them in lock-step.
  const buildSendPayload = () => {
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    const payload = {
      packet_id: selectedPacket.id,
      expiry_hours: expiryHours,
      requires_approval: requiresApproval,
    };
    if (isPlacementMode) {
      const cand = selectedPlacement.candidate;
      const cont = selectedPlacement.contact;
      payload.placement = {
        placement_id: selectedPlacement.id,
        candidate: cand && cand.id
          ? { name: cand.name || '', email: cand.email || '', bullhorn_id: cand.id, entity_type: 'candidate' }
          : null,
        contact: cont && cont.id
          ? {
              name: cont.name || '', email: cont.email || '', bullhorn_id: cont.id,
              entity_type: 'contact',
              company_id: selectedPlacement.company?.id || null,
              company_name: selectedPlacement.company?.name || null,
            }
          : null,
        company: selectedPlacement.company
          ? { bullhorn_id: selectedPlacement.company.id, name: selectedPlacement.company.name }
          : null,
      };
    } else {
      const recipientName = selectedRecipient.name ||
        `${selectedRecipient.firstName || ''} ${selectedRecipient.lastName || ''}`.trim();
      payload.recipient = {
        name: recipientName,
        email: selectedRecipient.email,
        bullhorn_id: selectedRecipient.id,
        entity_type: selectedPacket.entity_type,
        title: selectedRecipient.occupation || selectedRecipient.title || null,
        company_id: selectedRecipient.clientCorporation?.id || null,
        company_name: selectedRecipient.clientCorporation?.name || null,
      };
    }
    return payload;
  };

  // Dry-run: render preview PDFs without creating documents or sending
  // emails. Lets the recruiter validate field mappings against a real
  // Bullhorn record before committing to a live candidate send.
  const handleTestPreview = async () => {
    if (!selectedPacket) {
      toast.error('Please select a packet');
      return;
    }
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    if (isPlacementMode && !selectedPlacement) {
      toast.error('Please select a placement');
      return;
    }
    if (!isPlacementMode && !selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    setPreviewing(true);
    setPreviewResults(null);
    try {
      const res = await packetsApi.testPreview(buildSendPayload());
      setPreviewResults(res.data?.previews || []);
      toast.success(`Rendered ${res.data?.previews?.length || 0} preview PDF(s)`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Preview render failed'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedPacket) {
      toast.error('Please select a packet');
      return;
    }
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    if (isPlacementMode && !selectedPlacement) {
      toast.error('Please select a placement');
      return;
    }
    if (!isPlacementMode && !selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }

    setSending(true);
    try {
      const payload = buildSendPayload();

      const response = await packetsApi.send(payload);
      setSendResult(response.data);
      const recipientCount = (response.data?.recipients || []).length;
      const skipped = response.data?.skipped || [];
      if (skipped.length > 0) {
        toast.warning(
          `Sent to ${recipientCount} recipient(s). ${skipped.length} document(s) skipped — ${skipped[0].reason}.`
        );
      } else {
        toast.success('Packet sent successfully!');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send packet'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Success state
  if (sendResult) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="neumorphic rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Packet Sent Successfully!</h2>
            <p className="text-muted-foreground mb-6">
              {sendResult.documents_created} document(s) delivered
              {sendResult.recipients?.length > 1
                ? ` across ${sendResult.recipients.length} recipients.`
                : sendResult.recipients?.[0]
                  ? ` to ${sendResult.recipients[0].email}`
                  : sendResult.recipient
                    ? ` to ${sendResult.recipient}`
                    : ''}
            </p>

            {/* Per-recipient breakdown — only meaningful in placement mode */}
            {sendResult.recipients?.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                <p className="font-medium mb-2 text-blue-900 text-sm">Routed to:</p>
                <ul className="space-y-2">
                  {sendResult.recipients.map((r) => (
                    <li
                      key={`${r.route}-${r.email || r.name}`}
                      className="flex items-center gap-2 text-sm text-blue-900"
                    >
                      {r.route === 'contact' ? (
                        <Building2 className="w-4 h-4" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="font-medium">{r.name}</span>
                      <span className="text-blue-700">· {r.email}</span>
                      <span className="ml-auto text-xs text-blue-700">{r.documents} doc(s)</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sendResult.skipped?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left" data-testid="send-result-skipped">
                <p className="font-medium mb-2 text-amber-900 text-sm">Skipped:</p>
                <ul className="space-y-1 text-sm text-amber-800">
                  {sendResult.skipped.map((s) => (
                    <li key={`${s.template_id || s.template_name}-${s.reason}`}>
                      <span className="font-medium">{s.template_name}</span>
                      <span className="text-amber-700"> · {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-muted/30 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium mb-2">Documents sent:</p>
              <ul className="space-y-2">
                {sendResult.documents?.map((doc, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{doc.template_name}</span>
                    {doc.route && (
                      <span className="text-xs text-muted-foreground ml-auto">→ {doc.route}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setSendResult(null);
                setSelectedPacket(null);
                setSelectedRecipient(null);
                setSelectedPlacement(null);
              }}>
                Send Another
              </Button>
              <Button onClick={() => navigate(getTenantPath('/documents'))}>
                View Documents
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="send-packet-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(getTenantPath('/send'))}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Send Packet</h1>
            <p className="text-muted-foreground font-body">
              Send a collection of documents to a candidate or contact
            </p>
          </div>
        </div>

        {/* Warning if no Bullhorn connection */}
        {!bullhornConnected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Bullhorn not connected</p>
              <p className="text-sm text-amber-700">
                Connect to Bullhorn in Settings to search for candidates/contacts
              </p>
            </div>
          </div>
        )}

        {packets.length === 0 ? (
          <div className="neumorphic rounded-xl p-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-heading font-semibold text-lg mb-2">No packets available</h3>
            <p className="text-muted-foreground font-body mb-6">
              Create a packet first in the Templates section
            </p>
            <Button onClick={() => navigate(getTenantPath('/templates'))}>
              Go to Templates
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Packet Selection */}
            <div className="space-y-6">
              <div className="neumorphic rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Select Packet
                </h3>
                
                <Select value={selectedPacket?.id || ''} onValueChange={handlePacketSelect}>
                  <SelectTrigger data-testid="packet-select">
                    <SelectValue placeholder="Choose a packet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {packets.map(packet => (
                      <SelectItem key={packet.id} value={packet.id}>
                        <div className="flex items-center gap-2">
                          <span>{packet.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({packet.template_ids?.length || 0} docs)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedPacket && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      {selectedPacket.entity_type === 'placement' ? (
                        <Package className="w-4 h-4 text-purple-600" />
                      ) : selectedPacket.entity_type === 'contact' ? (
                        <Building2 className="w-4 h-4 text-[#F9A825]" />
                      ) : (
                        <User className="w-4 h-4 text-[#4CAF50]" />
                      )}
                      <span className="text-sm capitalize">{selectedPacket.entity_type} packet</span>
                    </div>
                    {selectedPacket.description && (
                      <p className="text-sm text-muted-foreground">{selectedPacket.description}</p>
                    )}
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground mb-2">Documents in this packet:</p>
                      <div className="space-y-1">
                        {selectedPacket.templates?.map(t => (
                          <div key={t.id} className="flex items-center gap-2 text-sm">
                            <FileText className="w-3 h-3 text-muted-foreground" />
                            {t.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Send Options */}
              <div className="neumorphic rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4">Send Options</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Expiry Time</Label>
                    <Select value={expiryHours.toString()} onValueChange={(v) => setExpiryHours(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                        <SelectItem value="72">72 hours</SelectItem>
                        <SelectItem value="168">1 week</SelectItem>
                        <SelectItem value="336">2 weeks</SelectItem>
                        <SelectItem value="720">30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="approval" 
                      checked={requiresApproval}
                      onCheckedChange={setRequiresApproval}
                    />
                    <Label htmlFor="approval" className="cursor-pointer">
                      Require admin approval before syncing to Bullhorn
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Recipient Selection */}
            <div className="neumorphic rounded-xl p-6">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                {selectedPacket?.entity_type === 'placement' ? (
                  <Package className="w-5 h-5 text-purple-600" />
                ) : selectedPacket?.entity_type === 'contact' ? (
                  <Building2 className="w-5 h-5 text-[#F9A825]" />
                ) : (
                  <User className="w-5 h-5 text-[#4CAF50]" />
                )}
                {selectedPacket?.entity_type === 'placement' ? 'Select Placement' : 'Select Recipient'}
              </h3>

              {!selectedPacket ? (
                <p className="text-muted-foreground text-sm">Select a packet first</p>
              ) : selectedPlacement ? (
                /* Placement-mode: show resolved candidate + contact so the
                   recruiter sees exactly who each document will go to. */
                <div className="space-y-4">
                  <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-4 space-y-3" data-testid="placement-resolved-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-purple-700">
                          <Package className="w-3.5 h-3.5" />
                          Placement #{selectedPlacement.id}
                        </div>
                        {selectedPlacement.jobOrder?.title && (
                          <p className="text-sm font-medium mt-0.5">{selectedPlacement.jobOrder.title}</p>
                        )}
                        {selectedPlacement.company?.name && (
                          <p className="text-xs text-muted-foreground">{selectedPlacement.company.name}</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedPlacement(null)} data-testid="placement-change-button">
                        Change
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-purple-200">
                      <div className={`rounded-md p-2.5 text-sm ${selectedPlacement.candidate?.email ? 'bg-white border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="placement-candidate-block">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 mb-1">
                          <User className="w-3 h-3" /> Candidate
                        </div>
                        {selectedPlacement.candidate?.email ? (
                          <>
                            <p className="font-medium truncate">{selectedPlacement.candidate.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{selectedPlacement.candidate.email}</p>
                          </>
                        ) : (
                          <p className="text-xs text-amber-800">No candidate on this placement</p>
                        )}
                      </div>
                      <div className={`rounded-md p-2.5 text-sm ${selectedPlacement.contact?.email ? 'bg-white border border-amber-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="placement-contact-block">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1">
                          <Building2 className="w-3 h-3" /> Contact
                        </div>
                        {selectedPlacement.contact?.email ? (
                          <>
                            <p className="font-medium truncate">{selectedPlacement.contact.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{selectedPlacement.contact.email}</p>
                          </>
                        ) : selectedPlacement.contact?.id ? (
                          /* Contact exists on the placement but has no email
                             on file in Bullhorn — we can't deliver so the
                             recruiter needs to update the contact record. */
                          <>
                            <p className="font-medium truncate">{selectedPlacement.contact.name || `Contact #${selectedPlacement.contact.id}`}</p>
                            <p className="text-xs text-amber-800 mt-0.5">
                              No email address on Bullhorn — update the contact record to send.
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-amber-800">No contact on this placement</p>
                        )}
                      </div>
                    </div>

                    {/* Routing preview — show which templates will go to whom */}
                    {selectedPacket.templates?.length > 0 && (
                      <div className="pt-2 border-t border-purple-200">
                        <p className="text-xs font-semibold text-purple-700 mb-1.5">Routing preview:</p>
                        <ul className="space-y-1 text-xs">
                          {selectedPacket.templates.map((t) => {
                            // Mirror worker/main routing: explicit contact/candidate
                            // templates route by entity; placement templates use flags.
                            const templateEntity = (t.entity_type || 'candidate').toLowerCase();
                            const sendToCandidate = templateEntity === 'placement'
                              ? t.send_to_candidate !== false
                              : templateEntity !== 'contact';
                            const sendToContact = templateEntity === 'placement'
                              ? !!t.send_to_contact
                              : templateEntity === 'contact';
                            const isContactOnly = sendToContact && !sendToCandidate;
                            const isCandidateOnly = sendToCandidate && !sendToContact;
                            const isBoth = sendToCandidate && sendToContact;
                            const missing = isContactOnly ? !selectedPlacement.contact?.email : !selectedPlacement.candidate?.email;
                            const reason = isContactOnly
                              ? (!selectedPlacement.contact?.id ? 'no contact' : 'no email')
                              : (!selectedPlacement.candidate?.id ? 'no candidate' : 'no email');
                            const label = isBoth ? 'Both' : isContactOnly ? 'Contact' : 'Candidate';
                            return (
                              <li key={t.id} className="flex items-center gap-1.5">
                                {missing ? (
                                  <AlertCircle className="w-3 h-3 text-amber-600" />
                                ) : isContactOnly ? (
                                  <Building2 className="w-3 h-3 text-[#F9A825]" />
                                ) : (
                                  <User className="w-3 h-3 text-[#4CAF50]" />
                                )}
                                <span className="truncate">{t.name}</span>
                                <span className="ml-auto text-muted-foreground">
                                  {missing ? `skipped — ${reason}` : `→ ${label}`}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  <Button
                    className="w-full btn-primary"
                    disabled={sending || previewing || (!selectedPlacement.candidate?.email && !selectedPlacement.contact?.email)}
                    onClick={handleSend}
                    data-testid="send-packet-button"
                  >
                    {sending ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Sending…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Packet ({selectedPacket.templates?.length || 0} documents)</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={sending || previewing}
                    onClick={handleTestPreview}
                    data-testid="test-preview-button"
                    title="Dry-run the packet: render each PDF with prefilled values from this placement, without creating documents or sending emails."
                  >
                    {previewing ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Rendering preview…</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Test preview (no send)</>
                    )}
                  </Button>
                </div>
              ) : selectedRecipient ? (
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {selectedRecipient.name || `${selectedRecipient.firstName || ''} ${selectedRecipient.lastName || ''}`.trim()}
                        </p>
                        <p className="text-sm text-muted-foreground">{selectedRecipient.email}</p>
                        {selectedRecipient.occupation && (
                          <p className="text-xs text-muted-foreground mt-1">{selectedRecipient.occupation}</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedRecipient(null)}>
                        Change
                      </Button>
                    </div>
                  </div>

                  <Button 
                    className="w-full btn-primary"
                    disabled={sending || previewing}
                    onClick={handleSend}
                    data-testid="send-packet-button"
                  >
                    {sending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Packet ({selectedPacket.templates?.length || 0} documents)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={sending || previewing}
                    onClick={handleTestPreview}
                    data-testid="test-preview-button-recipient"
                    title="Dry-run the packet: render each PDF with prefilled values for this recipient, without creating documents or sending emails."
                  >
                    {previewing ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Rendering preview…</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Test preview (no send)</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder={
                        selectedPacket.entity_type === 'placement'
                          ? 'Search placements by ID, candidate name or job title…'
                          : `Search ${selectedPacket.entity_type}s by name or email...`
                      }
                      className="pl-9"
                      data-testid="recipient-search"
                    />
                  </div>

                  {searching && (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                      {searchResults.map(result => {
                        if (selectedPacket.entity_type === 'placement') {
                          return (
                            <div
                              key={result.id}
                              className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => handlePlacementSelect(result)}
                              data-testid={`placement-result-${result.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100">
                                  <Package className="w-4 h-4 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">
                                    {result.jobOrder?.title || `Placement #${result.id}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {result.candidate?.name || '—'}
                                    {result.company?.name && ` · ${result.company.name}`}
                                  </p>
                                </div>
                                <span className="text-xs text-muted-foreground">{result.status}</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={result.id}
                            className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleRecipientSelect(result)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                selectedPacket.entity_type === 'contact' ? 'bg-[#F9A825]/10' : 'bg-[#4CAF50]/10'
                              }`}>
                                {selectedPacket.entity_type === 'contact' ? (
                                  <Building2 className="w-4 h-4 text-[#F9A825]" />
                                ) : (
                                  <User className="w-4 h-4 text-[#4CAF50]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {result.name || `${result.firstName || ''} ${result.lastName || ''}`.trim()}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{result.email}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No {selectedPacket.entity_type}s found
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Test-preview results panel — appears beneath the routing card
            once the recruiter clicks "Test preview". Lists each template
            with its prefill-stats and a link to open the rendered PDF. */}
        {previewResults && previewResults.length > 0 && (
          <div className="card-elevated p-6" data-testid="preview-results-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-600" /> Test preview results
              </h3>
              <button
                type="button"
                onClick={() => setPreviewResults(null)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Dismiss
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              These are dry-run PDFs — no documents were created and no emails were sent. Each row shows how many fields prefilled from Bullhorn. Low coverage usually means a template needs better field mappings.
            </p>
            <div className="space-y-2">
              {previewResults.map((p) => {
                const pct = p.total_field_count > 0 ? (p.prefilled_count / p.total_field_count) * 100 : 0;
                const tone =
                  pct >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                  pct >= 40 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                              'text-red-700 bg-red-50 border-red-200';
                return (
                  <div
                    key={p.template_id}
                    className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`preview-row-${p.template_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{p.template_name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Routed to <span className="capitalize">{p.route}</span>
                        {p.recipient_name && <> · {p.recipient_name}</>}
                      </p>
                    </div>
                    <span className={`text-[11px] px-2 py-1 rounded-full border font-medium whitespace-nowrap ${tone}`}>
                      {p.prefilled_count} / {p.total_field_count} fields prefilled
                    </span>
                    {p.preview_url ? (
                      <a
                        href={p.preview_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`preview-open-${p.template_id}`}
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open PDF
                        </Button>
                      </a>
                    ) : (
                      <span className="text-xs text-red-600 inline-flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> Render failed
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
